<template>
  <ValidationObserver ref="validator">
    <form ref="form" @submit.prevent="onSubmit" id="signup-b2b">
      <div class="row px-5 mb-5" data-testid="step1-b2b">
        <div v-if="showTransliteration" class="col-12 col-md-6">
          <Title id="personal-information" :text="translate('chPersonalInfo')" />
        </div>
        <div v-if="showTransliteration" class="col-12 col-md-6 d-none d-md-block">
          <Title id="personal-information" :text="translate('chPersonalInfoTrans')" />
          <i id="tooltip-target" class="icon-info-b2b xs ml-3"></i>
          <b-tooltip target="tooltip-target" triggers="hover">
            {{ translate('chPersonalInfoTooltip') }}
          </b-tooltip>
        </div>
        <Transliteration
          id="firstname"
          name="business.firstName"
          :label="translate('rwFirstName')"
          :rules="{ regex: FormRegex.INPUT_TEXT, required: true }"
          data-testid="firstname"
          :transRules="{
            regex: FormRegex.INPUT_TRANSLITERATE_CHAR_EXCEPTION,
            required: true
          }"
          :transLabel="showTransliteration ? translate('chNameUserLang') : translate('chName')"
          :classes="!showTransliteration ? '' : 'col-12 col-md-6'"
          :hidden="!showTransliteration"
        />
        <div v-if="showTransliteration" class="col-12 d-sm-block d-md-none">
          <Title id="personal-information" :text="translate('chPersonalInfoTrans')" />
          <i id="tooltip-target" class="icon-info-b2b xs ml-3"></i>
          <b-tooltip target="tooltip-target" triggers="focus">
            {{ translate('chPersonalInfoTooltip') }}
          </b-tooltip>
        </div>
        <Transliteration
          id="lastname"
          name="business.lastName"
          :label="translate('rwLastName')"
          :rules="{ regex: FormRegex.INPUT_TEXT, required: true }"
          data-testid="lastname"
          :transRules="{
            regex: FormRegex.INPUT_TRANSLITERATE_CHAR_EXCEPTION,
            required: true
          }"
          :transLabel="
            showTransliteration ? translate('chLastNameUserLang') : translate('chLastName')
          "
          :classes="!showTransliteration ? '' : 'col-12 col-md-6'"
          :hidden="!showTransliteration"
        />
        <div class="col-12 col-md-6">
          <SelectField
            id="country"
            name="business.countryCode"
            :placeholder="translate('rwCountryOfResidence')"
            :options="countries"
            rules="required"
            data-testid="country"
            v-on:changed="onChanged"
            :autocomplete="true"
          />
        </div>
        <div class="col-12 col-md-6">
          <SelectField
            id="lang"
            name="business.language"
            :placeholder="translate('rwLanguage')"
            :options="languages"
            rules="required"
            data-testid="language"
            :autocomplete="true"
          />
        </div>
        <div class="col-12 col-md-6">
          <TextField
            id="email"
            name="business.email"
            :label="translate('rwEmail')"
            type="email"
            :rules="{
              required: true,
              email: true,
              regex: '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}'
            }"
            data-testid="email"
            :show-icon="'email'"
          />
          <div v-if="isOcicoUpsellConsentFF" class="text-12">{{ translate('rwEmailOCIUpsellDisclaimer')}}</div>
        </div>
        <div class="col-12 col-md-6">
          <Phone
            name="business.telephone"
            :placeholder="`${translate('rwPhone')} (${translate('rwOptional')})`"
            data-testid="telephone"
            :defaultCountry="defaultCountry"
            disableFlags
            id="tel-b2b"
            :maxLen="30"
            :inputOptions="inputOptions"
            :enabledCountryCode="true"
          />
          <label v-if="isOcicoUpsellConsentFF" class="text-12">{{ translate('rwWhatsAppOCIDisclaimer') }}</label>
        </div>

        <div class="col-12 pt-4 pb-2"><Title text="password" /></div>
        <div class="col-12">
          <PasswordField id="password" name="business.password" />
        </div>

        <div class="col-12 pt-3">
          <Title id="personal-information" :text="translate('rwProfessionalRole')" />
        </div>

        <div class="col-12 pt-2 pb-3">
          <DynamicToggleSelector
            id="memberTypeSelector"
            :selectorsData="selectorsData"
            @toggle="onCheck"
          >
            <template v-slot:iata>
              <ValidationProvider tag="div" ref="iataNumber" v-slot="{ errors }" class="iata">
                <div class="iata__separator"></div>
                <span class="iata__title">{{
                  `${translate('rwIataNumber')} (${translate('rwOptional')})`
                }}</span>
                <span class="iata__description" v-html="translate('rwTravelAgentDescription')" />
                <div>
                  <TextField
                    id="iata"
                    name="business.iata"
                    :label="`${translate('rwIataNumber')} (${translate('rwOptional')})`"
                    data-testid="iata"
                    :show-icon="errors.iataError ? 'iataError' : ''"
                  />
                </div>
                <div v-if="errors.iataError" class="field__error iata__error">
                  {{ errors.iataError }}
                </div>
              </ValidationProvider>
            </template>
          </DynamicToggleSelector>
        </div>
        <div class="col-12 pt-3" v-if="promotionalCode">
          <TextField
            id="promotionalCode"
            name="business.promotionalCode"
            :label="translate('rwPromotionalCode')"
            :value="promotionalCode"
            :disabled="true"
            :hideIcon="true"
            data-testid="promotionalCode"
          />
        </div>
        <div class="col-12 text-center d-flex pt-5">
          <button
            class="btn btn-primary text-uppercase join-submit-btn text-bold"
            :class="loading ? 'm-progress' : ''"
            type="submit"
          >
            {{ translate('rwNext') }}
          </button>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import SelectField from '@/components/form-controls/SelectField'
import TextField from '@/components/form-controls/TextField'
import DynamicToggleSelector from './DynamicToggleSelector'
import Title from '@/components/form-controls/Title'
import Phone from '@/components/form-controls/Phone'
import PasswordField from '@/components/form-controls/PasswordField'
import formJson from 'form-json'
import { contentService, loyaltyService } from '../../infrastructure/Factory'
import { mapGetters, mapActions } from 'vuex'
import Transliteration from '../form-controls/Transliteration.vue'
import { FormRegex, isFlagActive } from '@/infrastructure/Configuration'
import showTransliteration from '@/components/mixins/showTransliteration'
import { getQueryParams } from '@/infrastructure/Utils'
export default {
  name: 'Step1',
  components: {
    PasswordField,
    ValidationObserver,
    ValidationProvider,
    Title,
    TextField,
    SelectField,
    Phone,
    DynamicToggleSelector,
    Transliteration
  },
  data: () => ({
    languages: [],
    countries: [],
    defaultCountry: '',
    selectorsData: [],
    loading: false,
    errors: '',
    FormRegex,
    inputOptions: {
      showDialCode: false
    },
    promotionalCode: '',
    isOcicoUpsellConsentFF: global.ConfigDynamic.isEnabled(global.ConfigDynamicKey.BOOKING_CICO_UPSELLING_CONSENT)
  }),
  mixins: [showTransliteration],
  methods: {
    ...mapActions(['trackErrorEventEnrollment', 'sendAnalyticsView']),
    async onSubmit(event) {
      event.preventDefault()

      const isOk = await this.$refs.validator.validate()

      const json = formJson(event.target)
      if (isOk) {
        if (json.business && json.business.iata) {
          try {
            this.loading = true
            const iata = await loyaltyService.validateIata(json.business.iata)
            this.$emit('next', { ...json, iata: iata })
          } catch {
            this.trackErrorEventEnrollment({ error: { iataError: ['IATA incorrect'], type: 'rewards_join_b2b_form' } })

            this.$refs.iataNumber.setErrors({ iataError: this.translate('rwIataIncorrect') })
          } finally {
            this.loading = false
          }
        } else {
          this.$emit('next', json)
        }
      } else {
        this.trackErrorEventEnrollment({ error: this.$refs.validator.errors, type: 'rewards_join_b2b_form' })
      }
    },
    onChanged(country) {
      this.defaultCountry = country
      this.$emit('countryChanged', country)
    },
    onCheck(index) {
      this.selectorsData[index].checked = !this.selectorsData[index].checked
    },
    getPromoCode() {
      if (isFlagActive('webextra.b2b.promocode.enrollment')) {
        const { promoCode } = getQueryParams(window.location.search)
        this.promotionalCode = promoCode
      }
    }
  },
  computed: {
    ...mapGetters(['userCountry'])
  },
  async created() {
    global.TrackingService.appendViewData({ pageName: 'radisson-rewards>join-b2b>step1' })
    this.sendAnalyticsView()
    this.defaultCountry = this.userCountry
    this.selectorsData = [
      {
        id: 'isMeetingPlanner',
        name: 'meetingPlanner',
        title: this.translate('rwIBookEvents'),
        subtitle: this.translate('rwIBookEventsDescription'),
        slot: false,
        checked: false
      },
      {
        id: 'isTravelAgent',
        name: 'travelAgent',
        title: this.translate('rwIBookRooms'),
        subtitle: this.translate('rwIBookRoomsDescription'),

        slot: 'iata',
        checked: false
      }
    ]
  },
  async mounted() {
    this.languages = await contentService.getLanguages()
    this.countries = await loyaltyService.getCountriesByMemberType('customer')
    this.getPromoCode()
  }
}
</script>

<style lang="scss">
.join-submit-btn {
  flex: auto;
  border-radius: 3rem;
  padding: 1.5rem 2.5rem !important;
}
.promo-code-message {
  font-size: 1.2rem;
}

.field__error {
  row-gap: 0.1rem;
  color: crimson;
  font-size: 1.1rem;
}

.iata {
  display: flex;
  flex-direction: column;

  &__separator {
    width: 100%;
    border: 1px solid #d8d8d8;
    margin: 2rem 0;
  }
  &__title {
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0.138462px;

    /* revamp/black */

    color: color('black-light');
  }
  &__error {
    margin-top: -0.75rem;
  }
  &__description {
    font-size: 10px;
    margin: 1rem 0 0.5rem 0;
  }
}
#tel-b2b{
  .vti__dropdown{
    border-right: 1px solid #d8d8d8;
  }
}
.pass-show {
  font-weight: 500;
   @media (min-width: media('tablet')) {
    font-weight: 600;
  }
}
</style>
